import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { useDebriefFactsState } from '../../shared/context/DebriefFactsStateContext';
import { STAGE_ORDER } from '../utils/stageMeta';
import { FactStageSection } from './FactStageSection';

interface GatherFactsPageProps {
  sessionId: string;
}

export function GatherFactsPage({ sessionId }: GatherFactsPageProps) {
  const { t } = useTranslation('debriefV2');
  const navigate = useNavigate();
  const { facts, isLoading } = useDebriefFactsState();

  const canContinue = !isLoading && facts.length > 0;

  const handleContinue = () => {
    if (!canContinue) return;
    navigate({
      to: '/interactions/debrief-v2/$id/match-points',
      params: { id: sessionId },
    });
  };

  return (
    <main className="flex-1 w-full max-w-[1040px] mx-auto pt-8 pb-24 px-6">
      <header className="mb-8 text-start">
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 mb-2">
          {t('gather.pageTitle')}
        </h2>
        <p className="text-slate-500 text-base">{t('gather.pageSubtitle')}</p>
      </header>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col">
        <div className="pt-4 pb-12 px-4 sm:px-10 relative">
          {isLoading ? (
            <PageSkeleton />
          ) : (
            STAGE_ORDER.map((stage, stageIndex) => (
              <FactStageSection
                key={stage}
                stage={stage}
                isLast={stageIndex === STAGE_ORDER.length - 1}
              />
            ))
          )}
        </div>

        <div className="px-5 md:px-8 py-4 bg-slate-50/50 border-t border-slate-200 flex justify-end items-center rounded-b-2xl">
          <button
            type="button"
            onClick={handleContinue}
            disabled={!canContinue}
            className={cn(
              'px-6 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors',
              canContinue
                ? 'bg-button-primary text-white hover:bg-button-primary-hover shadow-sm active:scale-[0.98] cursor-pointer'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed'
            )}
          >
            {t('gather.continueButton')}
            <ArrowRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
          </button>
        </div>
      </div>
    </main>
  );
}

function PageSkeleton() {
  return (
    <div className="space-y-10 py-4" aria-busy>
      {[0, 1, 2].map((stageIndex) => (
        <div key={stageIndex} className="relative pt-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-10 h-10 rounded-xl bg-slate-100 shrink-0" />
            <div className="h-5 bg-slate-100 rounded-md w-48 relative overflow-hidden">
              <ShimmerOverlay />
            </div>
          </div>
          <div className="ms-[56px] space-y-3">
            <SkeletonFactRow widthClass="w-3/4" />
            <SkeletonFactRow widthClass="w-1/2" />
            {stageIndex !== 1 && <SkeletonFactRow widthClass="w-2/3" />}
          </div>
        </div>
      ))}
    </div>
  );
}

interface SkeletonFactRowProps {
  widthClass: string;
}

function SkeletonFactRow({ widthClass }: SkeletonFactRowProps) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-1.5 h-1.5 rounded-full bg-slate-200 mt-2 shrink-0" />
      <div
        className={`h-4 bg-slate-100 rounded-md ${widthClass} relative overflow-hidden`}
      >
        <ShimmerOverlay />
      </div>
    </div>
  );
}

function ShimmerOverlay() {
  return (
    <div
      aria-hidden
      className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/70 to-transparent"
    />
  );
}
